import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { useOnceMutation } from './once-mutation';
import { getHttpClient } from '@vnetwork/margarita-core/http';
import type { Envelope } from './envelope';
import { iamKeys, iamProjectStatus, type IamProject } from './iam/types';

const foundationApi = getHttpClient('foundationApi');
const serviceApiV3 = getHttpClient('serviceApiV3');

// ============================================
// Types  (ported from legacy @console v3 service)
// ============================================

export type ProjectStatus = 'active' | 'suspended' | 'archived';
export type ProjectTeamRole = 'owner' | 'admin' | 'member' | 'viewer';

export interface ProjectMetadata {
  environment?: 'production' | 'staging' | 'development';
  region?: string;
  tags?: string[];
  [key: string]: unknown;
}

export interface ProjectSettings {
  default_region?: string;
  notifications_enabled?: boolean;
  [key: string]: unknown;
}

export interface Project {
  id: string;
  name: string;
  slug: string;
  description: string;
  organization_id: string;
  owner_email: string;
  /** The project this one sits under, if any. */
  parent_id?: string | null;
  status: ProjectStatus;
  metadata?: ProjectMetadata;
  settings?: ProjectSettings;
  teams_count?: number;
  resources_count?: number;
  created_at: string;
  updated_at: string;
  teams?: ProjectTeam[];
  /** Current user's role in this project. */
  user_role?: ProjectTeamRole;
}

export interface ProjectTeam {
  id: string;
  project_id: string;
  team_id: string;
  team_name?: string;
  team_description?: string;
  role: ProjectTeamRole;
  created_at: string;
  updated_at: string;
}

export interface ProjectResource {
  id: string;
  project_id: string;
  service_id: string;
  resource_type: string;
  resource_id: string;
  resource_name: string;
  resource_status: string;
  metadata?: Record<string, unknown>;
  assigned_by: string;
  assigned_at: string;
  synced_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateProjectRequest {
  name: string;
  description?: string;
  /** Create the project under this one; the caller needs projects.create on it. */
  parent_id?: string;
  metadata?: ProjectMetadata;
  settings?: ProjectSettings;
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string;
  status?: ProjectStatus;
  metadata?: ProjectMetadata;
  settings?: ProjectSettings;
}

/** A project as the IAM API returns it, in the shape the pages already use. */
export function iamProjectToProject(p: IamProject): Project {
  return {
    id: p.id,
    name: p.name,
    slug: p.slug,
    description: p.description,
    organization_id: p.org_id,
    owner_email: '',
    parent_id: p.parent_id ?? null,
    status: iamProjectStatus(p),
    created_at: p.created_at,
    updated_at: p.updated_at,
  };
}

// ============================================
// Query keys
// ============================================

const projectKeys = {
  all: ['v3', 'projects'] as const,
  project: (id: string) => ['v3', 'projects', id] as const,
  resources: (id: string) => ['v3', 'projects', id, 'resources'] as const,
  candidates: (id: string, params: DiscoverCandidatesParams) =>
    ['v3', 'projects', id, 'candidates', params] as const,
};

// ============================================
// Query hooks
// ============================================

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: projectKeys.project(id ?? ''),
    enabled: !!id,
    retry: false,
    queryFn: async (): Promise<Project | null> => {
      try {
        const res = await foundationApi.get<Envelope<IamProject>>(`/iam/projects/${id}`);
        return res.data?.data ? iamProjectToProject(res.data.data) : null;
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        // No access / not found are handled gracefully in the UI.
        if (status === 403 || status === 404) return null;
        throw error;
      }
    },
  });
}

// ============================================
// Mutation hooks
// ============================================

/** Invalidate the IAM project list + a single project's caches. */
function useProjectInvalidation() {
  const qc = useQueryClient();
  return {
    list: () => qc.invalidateQueries({ queryKey: iamKeys.projectsRoot }),
    project: (id: string) => qc.invalidateQueries({ queryKey: projectKeys.project(id) }),
  };
}

export function useCreateProject(
  options?: UseMutationOptions<Project, unknown, CreateProjectRequest>,
) {
  const inv = useProjectInvalidation();
  return useOnceMutation({
    mutationFn: async (body: CreateProjectRequest) => {
      const res = await foundationApi.post<Envelope<IamProject>>('/iam/projects', {
        name: body.name,
        description: body.description,
        parent_id: body.parent_id,
      });
      return iamProjectToProject(res.data.data);
    },
    ...options,
    onSuccess: (...args) => {
      inv.list();
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateProject(
  id: string,
  options?: UseMutationOptions<Project, unknown, UpdateProjectRequest>,
) {
  const inv = useProjectInvalidation();
  return useOnceMutation({
    mutationFn: async (body: UpdateProjectRequest) => {
      const res = await foundationApi.patch<Envelope<IamProject>>(`/iam/projects/${id}`, {
        name: body.name,
        description: body.description,
        status: body.status,
      });
      return iamProjectToProject(res.data.data);
    },
    ...options,
    onSuccess: (...args) => {
      inv.list();
      inv.project(id);
      options?.onSuccess?.(...args);
    },
  }, { keyExtra: [id] });
}

export function useDeleteProject(
  options?: UseMutationOptions<void, unknown, string>,
) {
  const inv = useProjectInvalidation();
  return useOnceMutation({
    mutationFn: async (id: string) => {
      await foundationApi.delete(`/iam/projects/${id}`);
    },
    ...options,
    onSuccess: (...args) => {
      inv.list();
      options?.onSuccess?.(...args);
    },
  });
}

/** Flip a project's `status` (archive ↔ restore). `mutate` takes the project id. */
function useProjectStatusMutation(
  status: ProjectStatus,
  options?: UseMutationOptions<Project, unknown, string>,
) {
  const inv = useProjectInvalidation();
  return useOnceMutation({
    mutationFn: async (id: string) => {
      const res = await foundationApi.patch<Envelope<IamProject>>(`/iam/projects/${id}`, { status });
      return iamProjectToProject(res.data.data);
    },
    ...options,
    onSuccess: (...args) => {
      const id = args[1];
      inv.list();
      inv.project(id);
      options?.onSuccess?.(...args);
    },
  }, { keyExtra: [status] });
}

/** Archive (soft-delete) a project. `mutate(projectId)`. */
export function useArchiveProject(
  options?: UseMutationOptions<Project, unknown, string>,
) {
  return useProjectStatusMutation('archived', options);
}

/** Restore an archived project. `mutate(projectId)`. */
export function useRestoreProject(
  options?: UseMutationOptions<Project, unknown, string>,
) {
  return useProjectStatusMutation('active', options);
}

// ============================================
// Auto Discovery — candidates + auto-assign
// These two stay on @mgmt (serviceApiV3): they query the instances/domains tables,
// which margarita-foundation does not own (foundation#64). Both services share one
// DB for now, so they keep working against the same project_resources rows.
// ============================================

export type CandidateResourceType = 'domain' | 'instance';
export type CandidateAssignment = 'unassigned' | 'this_project' | 'other_project';

export interface CandidateResource {
  service_id: string;
  resource_type: CandidateResourceType;
  resource_id: string;
  resource_name: string;
  resource_status: string;
  tags: string[];
  created_at: string;
  assignment: CandidateAssignment;
  assigned_project_id?: string;
  assigned_project_name?: string;
}

export interface CandidateCounts {
  unassigned: number;
  this_project: number;
  other_project: number;
}

export interface CandidatesResponse {
  items: CandidateResource[];
  total: number;
  counts: CandidateCounts;
  page: number;
  per_page: number;
}

/** Query params for the candidates endpoint. CSV fields are joined before send. */
export interface DiscoverCandidatesParams {
  /** One or both of 'domain' | 'instance'. Omitted → backend defaults to both. */
  resource_types?: CandidateResourceType[];
  status?: string;
  search?: string;
  tags?: string[];
  assignment?: 'all' | 'unassigned';
  created_from?: string;
  created_to?: string;
  page?: number;
  per_page?: number;
}

/** Server-side filter block shared by candidates + auto-assign. */
export interface AutoAssignFilters {
  resource_types?: string[];
  status?: string;
  search?: string;
  tags?: string[];
  created_from?: string;
  created_to?: string;
}

export interface AutoAssignRequest {
  filters: AutoAssignFilters;
  include_ids?: string[];
  exclude_ids?: string[];
  dry_run: boolean;
}

export interface AutoAssignConflict {
  resource_id: string;
  resource_type: string;
  project_id: string;
  project_name: string;
}

export interface AutoAssignResult {
  matched: number;
  assigned: number;
  skipped_already_in_project: number;
  conflicts: AutoAssignConflict[];
  dry_run: boolean;
}

/** Build the axios query object from typed params (CSV-joining list fields). */
function candidatesQuery(params: DiscoverCandidatesParams): Record<string, string | number> {
  const q: Record<string, string | number> = {};
  if (params.resource_types?.length) q.resource_type = params.resource_types.join(',');
  if (params.status) q.status = params.status;
  if (params.search) q.search = params.search;
  if (params.tags?.length) q.tags = params.tags.join(',');
  if (params.assignment) q.assignment = params.assignment;
  if (params.created_from) q.created_from = params.created_from;
  if (params.created_to) q.created_to = params.created_to;
  if (params.page) q.page = params.page;
  if (params.per_page) q.per_page = params.per_page;
  return q;
}

export interface BulkResourceItem {
  service_id: string;
  resource_type: string;
  resource_id: string;
  resource_name?: string;
}

/** Bulk assign/unassign resources to a project (POST /projects/:id/resources/bulk). */
export function useBulkAssignProjectResources(
  options?: UseMutationOptions<
    unknown,
    unknown,
    { projectId: string; action: 'assign' | 'unassign'; resources: BulkResourceItem[] }
  >,
) {
  const qc = useQueryClient();
  return useOnceMutation({
    mutationFn: async ({ projectId, action, resources }) => {
      const res = await foundationApi.post(`/projects/${projectId}/resources/bulk`, { action, resources });
      return res.data?.data ?? null;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: projectKeys.resources(args[1].projectId) });
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Auto Discovery hooks
// ============================================

type DiscoverQueryOptions = Omit<
  UseQueryOptions<CandidatesResponse>,
  'queryKey' | 'queryFn'
>;

/**
 * Discover assignable resources (CDN domains + compute instances) for a project,
 * filtered by conditions. Paginated; previous page data is kept while the next
 * page loads to avoid layout flashes.
 */
export function useDiscoverCandidates(
  projectId: string | undefined,
  params: DiscoverCandidatesParams,
  options?: DiscoverQueryOptions,
) {
  return useQuery<CandidatesResponse>({
    queryKey: projectKeys.candidates(projectId ?? '', params),
    enabled: !!projectId && (options?.enabled ?? true),
    placeholderData: keepPreviousData,
    ...options,
    queryFn: async () => {
      const res = await serviceApiV3.get<Envelope<CandidatesResponse>>(
        `/projects/${projectId}/resources/candidates`,
        { params: candidatesQuery(params) },
      );
      return (
        res.data?.data ?? {
          items: [],
          total: 0,
          counts: { unassigned: 0, this_project: 0, other_project: 0 },
          page: params.page ?? 1,
          per_page: params.per_page ?? 50,
        }
      );
    },
  });
}

/**
 * Auto-assign matching resources to a project. Supports `dry_run` (preview the
 * matched/assigned/conflict counts without committing) and real commits. On a
 * committed (non-dry) success, the project's resource + candidate caches are
 * invalidated.
 */
export function useAutoAssign(
  projectId: string,
  options?: UseMutationOptions<AutoAssignResult, unknown, AutoAssignRequest>,
) {
  const qc = useQueryClient();
  return useOnceMutation<AutoAssignResult, unknown, AutoAssignRequest>({
    mutationFn: async (body: AutoAssignRequest) => {
      const res = await serviceApiV3.post<Envelope<AutoAssignResult>>(
        `/projects/${projectId}/resources/auto-assign`,
        body,
      );
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      const [, request] = args;
      if (!request.dry_run) {
        qc.invalidateQueries({ queryKey: projectKeys.resources(projectId) });
        qc.invalidateQueries({ queryKey: ['v3', 'projects', projectId, 'candidates'] });
      }
      options?.onSuccess?.(...args);
    },
  }, { keyExtra: [projectId] });
}
